import { AxiosResponse } from 'axios';
import api from '~/utils/api';
import { Pagination, SelectItem } from '~/types';
import {
  CreateZeroCarbonLetter,
  ZeroCarbonLetter,
  ZeroCarbonLetterView,
  ZeroCarbonProject,
  ZeroCarbonProjectInvoiceView,
  ZeroCarbonProjectView,
} from '~/types/zeroCarbon';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { formatCurrency } from '~/utils/formatCurrency';

export async function getZeroCarbonProjects(
  page: number,
  size: number,
): Promise<Pagination<ZeroCarbonProjectView>> {
  const { data } = await api.get<Pagination<ZeroCarbonProjectView>>(
    '/projetos_co2',
    {
      params: {
        page,
        size,
      },
    },
  );

  const formatted = data.results.map(project => ({
    ...project,
    formattedQuantidadeCo2Contratado: Number(
      project.quantidade_co2_contratado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedQuantidadeCo2Disponivel: Number(
      project.quantidade_co2_disponivel,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedQuantidadeCo2Utilizado: Number(
      project.quantidade_co2_utilizado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
  }));

  return { ...data, results: formatted };
}

export async function getZeroCarbonProjectsAsSelect(): Promise<SelectItem[]> {
  const { data } = await api.get<Pagination<ZeroCarbonProjectView>>(
    '/projetos_co2',
    {
      params: {
        page: 1,
        size: 9999,
      },
    },
  );

  const formatted = data.results.map(project => ({
    label: project.nome,
    value: project.id.toString(),
  }));

  return formatted;
}

export async function createZeroCarbonProject(data: any): Promise<any> {
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };
  const response = await api.post('/projetos_co2', data, config);

  return response;
}

export async function getZeroCarbonProjectById(
  id: number,
): Promise<ZeroCarbonProjectView> {
  const { data } = await api.get<ZeroCarbonProjectView>(`/projetos_co2/${id}`);

  const formatted = {
    ...data,
    formattedQuantidadeCo2Contratado: Number(
      data.quantidade_co2_contratado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedQuantidadeCo2Disponivel: Number(
      data.quantidade_co2_disponivel,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedQuantidadeCo2Utilizado: Number(
      data.quantidade_co2_utilizado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
  };

  return formatted;
}

export async function getProjectInvoice(
  id: number,
): Promise<ZeroCarbonProjectInvoiceView[]> {
  const { data } = await api.get<ZeroCarbonProjectInvoiceView[]>(
    `/projetos_co2/${id}/notas_fiscais`,
  );

  const formatted = data.map(invoice => ({
    ...invoice,
    formattedDataCompra: formatInTimeZone(
      `${invoice.data_compra}`,
      'dd/MM/yyyy',
    ),
    formattedValor: formatCurrency(Number(invoice?.valor)),
    formattedQuantidadeCo2Contratado: Number(
      invoice.quantidade_co2_contratado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
  }));

  return formatted;
}

export async function createProjectInvoice(
  id: number,
  data: any,
): Promise<any> {
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };
  const response = await api.post(
    `/projetos_co2/${id}/add_nota_fiscal`,
    data,
    config,
  );

  return response;
}

export async function deleteZeroCarbonProjet(
  id: number,
): Promise<ZeroCarbonProjectView> {
  const response = await api.delete<ZeroCarbonProjectView>(
    `/projetos_co2/${id}`,
  );

  return response.data;
}

export async function deleteProjectInvoice(
  invoiceId: number,
  projectId: number,
): Promise<any> {
  const response = await api.delete(
    `/projetos_co2/${projectId}/delete_nota_fiscal/${invoiceId}`,
  );

  return response;
}

export async function downloadProjectFile(
  id: number | string,
): Promise<AxiosResponse> {
  return api.get(`/projetos_co2/${id}/download_apresentacao`, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function downloadProjectInvoiceFile(
  invoiceId: number,
  projectId: number,
): Promise<AxiosResponse> {
  return api.get(
    `/projetos_co2/${projectId}/download_nota_fiscal/${invoiceId}`,
    {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'blob' as 'json',
    },
  );
}

export async function getZeroCarbonProjetById(
  id: string,
): Promise<ZeroCarbonProject> {
  const { data } = await api.get<ZeroCarbonProject>(`/projetos_co2/${id}`);
  return data;
}

export async function getZeroCarbonLetters(
  page: number,
  size: number,
): Promise<Pagination<ZeroCarbonLetterView>> {
  const { data } = await api.get<Pagination<ZeroCarbonLetterView>>(
    '/cartas_co2',
    {
      params: {
        page,
        size,
      },
    },
  );

  const formatted = data.results.map(letter => ({
    ...letter,
    formattedDataAtualizacao: formatInTimeZone(
      `${letter.data_atualizacao}`,
      'dd/MM/yyyy',
    ),
  }));
  return { ...data, results: formatted };
}

export async function deleteZeroCarbonLetter(
  id: number,
): Promise<ZeroCarbonLetter> {
  const response = await api.delete<ZeroCarbonLetter>(`/cartas_co2/${id}`);
  return response.data;
}

export async function createZeroCarbonLetter(
  data: CreateZeroCarbonLetter,
): Promise<any> {
  const response = await api.post('/cartas_co2', data);
  return response;
}

export async function updateZeroCarbonLetter(
  data: CreateZeroCarbonLetter,
): Promise<any> {
  const response = await api.put(`/cartas_co2/${data.id}`, data);
  return response;
}

export async function getZeroCarbonLetterById(
  id: number,
): Promise<ZeroCarbonLetter> {
  const { data } = await api.get<ZeroCarbonLetter>(`/cartas_co2/${id}`);
  return data;
}

export async function getProjectsXlsx(): Promise<AxiosResponse<any>> {
  const url = '/projetos_co2/xlsx';
  return api.get(url, {
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
